// Field names match the GET /payment/combine-bundle/{eventId} contract
// (CombineBundleOfferVM). Every price, total and saving shown here comes
// straight from the route — nothing is recalculated in the browser.
export const getBundleId = (bundle) => bundle?.combineBundleId;
export const getBundleName = (bundle) => bundle?.name ?? "Combine Bundle";
export const getCombines = (bundle) =>
  Array.isArray(bundle?.combines) ? bundle.combines : [];
export const getBundleCombineCount = (bundle) =>
  bundle?.combineCount ?? getCombines(bundle).length;
export const getTotalSeparatePrice = (bundle) =>
  Number(bundle?.totalIfBoughtSeparately ?? 0);
// A percent-off bundle price can come back with more than 2 decimals
// (e.g. 134.388), which IntelliPay shows as $0, so it is rounded to cents.
export const getBundlePrice = (bundle) =>
  Math.round((Number(bundle?.bundlePrice ?? 0) + Number.EPSILON) * 100) / 100;
// getBundlePrice reads a missing price as 0, which would register the bundle
// through the free path, so the offer is only shown for a real price.
export const hasValidBundlePrice = (bundle) => {
  const raw = bundle?.bundlePrice;
  if (raw === null || raw === undefined || raw === "") return false;
  const price = Number(raw);
  return Number.isFinite(price) && price >= 0;
};
export const getSavingsAmount = (bundle) => Number(bundle?.savingsAmount ?? 0);
export const getSavingsPercent = (bundle) =>
  Number(bundle?.savingsPercent ?? 0);
export const getDrillCombineCount = (bundle) =>
  bundle?.combinesWithPositionDrillsCount ??
  getCombines(bundle).filter(getHasPositionDrills).length;

export const getCombineEventId = (combine) => combine?.eventId;
export const getCombineName = (combine) => combine?.name ?? "Combine";
export const getCombineLocation = (combine) =>
  [combine?.city, combine?.state].filter(Boolean).join(", ");
export const getCombinePrice = (combine) => Number(combine?.combinePrice ?? 0);
export const getHasPositionDrills = (combine) =>
  Boolean(combine?.hasPositionDrills);
export const getPositionDrillPrice = (combine) =>
  Number(combine?.positionDrillPrice ?? 0);
export const isSelectedCombine = (combine) => Boolean(combine?.isSelected);

export const formatMoney = (value) => `$${Number(value ?? 0).toFixed(2)}`;
export const formatSavings = (value) => {
  const amount = Number(value ?? 0);
  return Number.isInteger(amount) ? `$${amount}` : formatMoney(amount);
};
export const pluralize = (count, word) =>
  `${count} ${word}${count === 1 ? "" : "s"}`;
export const pad2 = (value) => String(value ?? 0).padStart(2, "0");

export const formatDate = (date) => {
  if (!date) return "";
  if (typeof date === "object") {
    if (typeof date.year !== "number") return "";
    return `${pad2(date.month)}-${pad2(date.day)}-${date.year}`;
  }
  const [year, month, day] = String(date).split("T")[0].split("-");
  if (!year || !month || !day) return "";
  return `${month}-${day}-${year}`;
};

export const formatTime = (time) => {
  if (!time) return "";
  let hour;
  let minute;
  if (typeof time === "object") {
    hour = time.hour;
    minute = time.minute;
  } else {
    [hour, minute] = String(time).split(":").map(Number);
  }
  if (typeof hour !== "number" || Number.isNaN(hour)) return "";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const period = hour >= 12 ? "PM" : "AM";
  return `${hour12}:${pad2(Number.isNaN(minute) ? 0 : minute)} ${period}`;
};

export const getCombineDate = (combine) => formatDate(combine?.date);

export const getCombineTime = (combine) => {
  const start = formatTime(combine?.startTime);
  const end = formatTime(combine?.endTime);
  if (start && end) return `${start} - ${end}`;
  return start || end;
};

export const OPTION_SINGLE = "single";
export const OPTION_BUNDLE = "bundle";

// The route flags the combine the athlete picked with isSelected; the event id
// is the fallback when the flag is missing.
export const resolveSelectedCombine = (bundle, eventId) => {
  const combines = getCombines(bundle);
  return (
    combines.find(isSelectedCombine) ??
    combines.find(
      (combine) => String(getCombineEventId(combine)) === String(eventId),
    )
  );
};

// Without a resolvable selected combine the single-event price would render
// as $0.00, and without a valid bundle price the bundle could not be charged
// correctly, so such an offer is treated the same as no bundle.
export const isBundleOfferUsable = (bundle, eventId) =>
  Boolean(
    bundle &&
      resolveSelectedCombine(bundle, eventId) &&
      hasValidBundlePrice(bundle),
  );
